import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CalendarOutlined, CustomerServiceOutlined, DownOutlined, FileTextOutlined, InfoCircleOutlined, UpOutlined } from "@ant-design/icons";
import { Tooltip, theme } from "antd";
import { observer } from "mobx-react";
import { EProductPlanPaymentInterval } from "@devowl-wp/api-real-commerce";
import { ProductLogo } from "./productLogo.js";
import { SubscriptionActionsOverflow } from "./subscriptionActionsOverflow.js";
import { SubscriptionContextualCta } from "./subscriptionContextualCta.js";
import { SubscriptionStatusPill } from "./subscriptionStatusPill.js";
import { formatAmount } from "../../../../../utils/formatAmount.js";
import { formatLocaleDate } from "../../../../../utils/formatLocaleDate.js";
import { __ } from "../../../../../utils/i18n.js";
const SubscriptionCardHeader = observer(({ subscription, isExpanded, onToggle })=>{
    const { token } = theme.useToken();
    const { productPlan: { product: { name }, description: planDescription, paymentInterval, price }, ecommerceSystemSubscriptionId, supportUntil, currency } = subscription.data;
    // For one-time / lifetime plans the displayed amount is the plan list price in the subscription's own currency.
    const recurringPrice = price?.find((productPlanPrice)=>productPlanPrice.currency === currency)?.recurringPrice;
    // Skip the price suffix for free products (recurringPrice === 0): a
    // "0.00 € once" tail adds no information and reads as noise on free
    // lifetime / one-time plans.
    const { taxTreatment } = subscription;
    const numericPrice = +recurringPrice;
    let priceLine = planDescription;
    if (subscription.isLifetime) {
        priceLine = numericPrice ? `${planDescription} · ${formatAmount({
            amount: numericPrice,
            currency,
            taxTreatment
        })} ${__("once")}` : planDescription;
    } else if (paymentInterval === EProductPlanPaymentInterval.OneTime) {
        priceLine = ecommerceSystemSubscriptionId && numericPrice ? `${planDescription} · ${formatAmount({
            amount: numericPrice,
            currency,
            taxTreatment
        })} ${__("once")}` : planDescription;
    } else {
        // Recurring plans show the contract price (what the customer signed up at), falling back to the upcoming
        // charge for legacy rows that have no contract snapshot yet. The plan list price (price[0]) is neither.
        // currentRecurringPriceLabel already carries the net/gross qualifier.
        const recurringLabel = subscription.currentRecurringPriceLabel;
        priceLine = recurringLabel ? `${planDescription} · ${recurringLabel}` : planDescription;
    }
    // When the upcoming charge diverges from the contract price, stack both within the price component — the next
    // renewal prominent, the ongoing contract price struck through and smaller beneath it — so the price grows
    // vertically (and stays centered in the meta row) instead of widening and pushing a third line.
    const isDivergentBilling = subscription.hasDivergentNextBilling && !!subscription.nextBillingChargeLabel && !!subscription.contractPriceLabel;
    const { kind, date: primaryDate } = subscription.primaryDateLabel;
    let primaryDateText;
    if (kind === "lifetime") {
        primaryDateText = __("Lifetime purchase");
    } else if (kind === "renews") {
        primaryDateText = __("Renews at %s", formatLocaleDate(primaryDate));
    } else if (kind === "valid") {
        primaryDateText = __("Valid until %s", formatLocaleDate(primaryDate));
    } else {
        primaryDateText = __("Access ends on %s", formatLocaleDate(primaryDate));
    }
    const stopPropagation = (e)=>e.stopPropagation();
    // Cancelled subscriptions use a muted icon colour to match the greyed-out
    // logo + text — the card reads as archived, brand-blue icons would fight
    // that hierarchy.
    const metaIconColor = subscription.isCancelled ? token.colorTextTertiary : token.colorPrimary;
    return /*#__PURE__*/ _jsxs("div", {
        className: "rc-account-v2-subscription-card__header",
        role: "button",
        tabIndex: 0,
        "aria-expanded": isExpanded,
        onClick: onToggle,
        onKeyDown: (e)=>{
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onToggle();
            }
        },
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "rc-account-v2-subscription-card__header-left",
                children: [
                    /*#__PURE__*/ _jsx(ProductLogo, {
                        subscription: subscription
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "rc-account-v2-subscription-card__header-text",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "rc-account-v2-subscription-card__title-row",
                                children: [
                                    /*#__PURE__*/ _jsx("h2", {
                                        className: "rc-account-v2-subscription-card__product-name",
                                        style: {
                                            color: token.colorTextHeading
                                        },
                                        children: name
                                    }),
                                    /*#__PURE__*/ _jsx(SubscriptionStatusPill, {
                                        subscription: subscription
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "rc-account-v2-subscription-card__meta",
                                style: {
                                    color: token.colorTextSecondary
                                },
                                children: [
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "rc-account-v2-subscription-card__meta-item",
                                        children: [
                                            /*#__PURE__*/ _jsx(FileTextOutlined, {
                                                style: {
                                                    color: metaIconColor
                                                },
                                                "aria-hidden": true
                                            }),
                                            isDivergentBilling ? /*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    planDescription,
                                                    " \xb7",
                                                    " ",
                                                    /*#__PURE__*/ _jsx(Tooltip, {
                                                        title: __("Your next renewal is discounted to %s instead of your usual %s.", subscription.nextBillingChargeLabel, subscription.contractPriceLabel),
                                                        children: /*#__PURE__*/ _jsxs("span", {
                                                            className: "rc-account-v2-subscription-card__price-hint",
                                                            children: [
                                                                subscription.nextBillingChargeLabel,
                                                                " ",
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "rc-account-v2-subscription-card__price-next-tag",
                                                                    style: {
                                                                        color: token.colorTextTertiary
                                                                    },
                                                                    children: __("next renewal")
                                                                }),
                                                                " ",
                                                                /*#__PURE__*/ _jsx(InfoCircleOutlined, {
                                                                    className: "rc-account-v2-subscription-card__price-info",
                                                                    style: {
                                                                        color: token.colorTextTertiary
                                                                    }
                                                                })
                                                            ]
                                                        })
                                                    })
                                                ]
                                            }) : /*#__PURE__*/ _jsx("span", {
                                                children: priceLine
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "rc-account-v2-subscription-card__meta-sep",
                                        "aria-hidden": true,
                                        children: "\xb7"
                                    }),
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "rc-account-v2-subscription-card__meta-item",
                                        children: [
                                            /*#__PURE__*/ _jsx(CalendarOutlined, {
                                                style: {
                                                    color: metaIconColor
                                                },
                                                "aria-hidden": true
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                children: primaryDateText
                                            })
                                        ]
                                    }),
                                    !!supportUntil && !subscription.isPastDue && /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "rc-account-v2-subscription-card__meta-sep",
                                                "aria-hidden": true,
                                                children: "\xb7"
                                            }),
                                            /*#__PURE__*/ _jsxs("span", {
                                                className: "rc-account-v2-subscription-card__meta-item",
                                                children: [
                                                    /*#__PURE__*/ _jsx(CustomerServiceOutlined, {
                                                        style: {
                                                            color: metaIconColor
                                                        },
                                                        "aria-hidden": true
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        children: __("Support until %s", formatLocaleDate(supportUntil))
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "rc-account-v2-subscription-card__header-right",
                onClick: stopPropagation,
                role: "presentation",
                children: [
                    /*#__PURE__*/ _jsx(SubscriptionContextualCta, {
                        subscription: subscription
                    }),
                    /*#__PURE__*/ _jsx(SubscriptionActionsOverflow, {
                        subscription: subscription
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "rc-account-v2-subscription-card__chevron",
                        style: {
                            color: token.colorTextSecondary
                        },
                        "aria-hidden": true,
                        onClick: (e)=>{
                            e.stopPropagation();
                            onToggle();
                        },
                        children: isExpanded ? /*#__PURE__*/ _jsx(UpOutlined, {}) : /*#__PURE__*/ _jsx(DownOutlined, {})
                    })
                ]
            })
        ]
    });
});
export { SubscriptionCardHeader };
